/**
 * Stale-While-Revalidate cache
 *
 * Strategy:
 * 1. Return cached data immediately (fast UX, no spinner on repeat visits)
 * 2. ALWAYS fire a background network request in parallel
 * 3. When fresh data arrives, update the cache
 * 4. Any component subscribed via useCachedFetch() re-renders with fresh data
 *
 * This means:
 * - First visit: normal network fetch (shows skeleton)
 * - Repeat visits: instant from cache, silently refreshes in background
 * - Data changes in API: visible within the SAME page view (after ~1-2s background fetch)
 * - No stale data problem, no 30min TTL issue
 */


// ─── Cache version — bump this when API response shape changes ────────────
const CACHE_VERSION = 'v2_versions';
const LS_VERSION_KEY = 'iiis_cache_ver';
(function() {
  try {
    if (localStorage.getItem(LS_VERSION_KEY) !== CACHE_VERSION) {
      Object.keys(localStorage)
        .filter(k => k.startsWith('iiis_swr_'))
        .forEach(k => localStorage.removeItem(k));
      localStorage.setItem(LS_VERSION_KEY, CACHE_VERSION);
    }
  } catch(e) {}
})();
// ──────────────────────────────────────────────────────────────────────────

const memoryCache = new Map();       // url -> data
const inflightRequests = new Map();  // url -> Promise (dedup)
const subscribers = new Map();       // url -> Set of callbacks

const LS_PREFIX = 'iiis_swr_';
const LS_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days (only used as fallback if offline)

// ── localStorage helpers ───────────────────────────────────────────────────
function lsKey(url) {
  return LS_PREFIX + url.replace(/[^a-z0-9]/gi, '_').slice(0, 80);
}

function lsRead(url) {
  try {
    const raw = localStorage.getItem(lsKey(url));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    // Only reject if older than 7 days (emergency fallback)
    if (Date.now() - ts > LS_MAX_AGE) { localStorage.removeItem(lsKey(url)); return null; }
    return data;
  } catch { return null; }
}

function lsWrite(url, data) {
  try {
    localStorage.setItem(lsKey(url), JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // Storage full — evict oldest half of our keys
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith(LS_PREFIX));
      keys.slice(0, Math.ceil(keys.length / 2)).forEach(k => localStorage.removeItem(k));
      localStorage.setItem(lsKey(url), JSON.stringify({ data, ts: Date.now() }));
    } catch {}
  }
}

// ── Notify subscribers when data changes ──────────────────────────────────
function notify(url, data) {
  const cbs = subscribers.get(url);
  if (cbs) cbs.forEach(cb => cb(data));
}

// ── Background revalidation ────────────────────────────────────────────────
function revalidate(url) {
  // Skip if already in-flight
  if (inflightRequests.has(url)) return inflightRequests.get(url);

  const promise = fetch(url)
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
    .then(fresh => {
      const old = memoryCache.get(url);
      // Only update & notify if data actually changed
      const changed = JSON.stringify(old) !== JSON.stringify(fresh);
      memoryCache.set(url, fresh);
      lsWrite(url, fresh);
      inflightRequests.delete(url);
      if (changed) notify(url, fresh);
      return fresh;
    })
    .catch(err => {
      inflightRequests.delete(url);
      // On network error, keep serving cached data — don't throw
      return memoryCache.get(url) || null;
    });

  inflightRequests.set(url, promise);
  return promise;
}

// ── Main fetch function ────────────────────────────────────────────────────
/**
 * Returns data as fast as possible (cache or network), then always
 * revalidates in background so next render gets fresh data.
 */
export async function cachedFetch(url) {
  // 1. Check memory cache
  if (memoryCache.has(url)) {
    // Serve from memory, revalidate in background
    revalidate(url);
    return memoryCache.get(url);
  }

  // 2. Check localStorage (survives page refresh)
  const stored = lsRead(url);
  if (stored !== null) {
    memoryCache.set(url, stored);
    // Serve from storage, revalidate in background
    revalidate(url);
    return stored;
  }

  // 3. First visit — must wait for network
  return revalidate(url);
}

// ── Subscribe to live updates for a URL ───────────────────────────────────
/**
 * Components call this to be notified when background revalidation
 * returns fresh data. Returns an unsubscribe function.
 */
export function subscribe(url, callback) {
  if (!subscribers.has(url)) subscribers.set(url, new Set());
  subscribers.get(url).add(callback);
  return () => {
    const set = subscribers.get(url);
    if (set) { set.delete(callback); if (set.size === 0) subscribers.delete(url); }
  };
}

// ── Force refresh (e.g. after user action) ────────────────────────────────
export function invalidateCache(url) {
  memoryCache.delete(url);
  try { localStorage.removeItem(lsKey(url)); } catch {}
}

export function invalidateAll() {
  memoryCache.clear();
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(LS_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch {}
}

// ── Prefetch (fire-and-forget) ─────────────────────────────────────────────
export function prefetch(urls) {
  urls.forEach(url => { cachedFetch(url).catch(() => {}); });
}

// ── Current cached value (sync, no fetch) ─────────────────────────────────
export function getCached(url) {
  return memoryCache.get(url) ?? lsRead(url);
}